import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  type ConfigBackupRestoreResponse,
  type ClearCapturedContentResponse,
  type ContentScraperSyncResponse,
  type ConfigRestoreReport,
  type PauseResponse,
  type RunResponse,
  type SourceConfig,
  type SourceCreateRequest,
  type SourceOperations,
  type SourceOperationsUpdateRequest,
  type SourceUpdateRequest,
} from "@/lib/api";

const SOURCES_KEY = ["sources"] as const;
const sourceOperationsKey = (sourceId: string) => ["source-operations", sourceId] as const;

export interface PauseArgs {
  sourceId: string;
  /** Relative pause in seconds. Mutually exclusive with `until`. */
  durationSeconds?: number;
  /** Explicit ISO timestamp. Mutually exclusive with `durationSeconds`. */
  until?: string;
  reason?: string;
}

export function useSources() {
  return useQuery<SourceConfig[]>({
    queryKey: SOURCES_KEY,
    queryFn: () => apiGet<SourceConfig[]>("/api/v1/sources"),
  });
}

/** Restore DB source config from the YAML backup/seed files. */
export function useRestoreSourcesFromYaml() {
  const qc = useQueryClient();
  return useMutation<ConfigRestoreReport>({
    mutationFn: () =>
      apiPost<ConfigRestoreReport>("/api/v1/sources/restore/yaml", {
        confirm: "RESTORE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SOURCES_KEY });
    },
  });
}

export function useRestoreConfigBackup() {
  const qc = useQueryClient();
  return useMutation<ConfigBackupRestoreResponse, Error, Record<string, unknown>>({
    mutationFn: (backup) =>
      apiPost<ConfigBackupRestoreResponse>("/api/v1/backups/config/restore", {
        confirm: "RESTORE_CONFIG",
        backup,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SOURCES_KEY });
      qc.invalidateQueries({ queryKey: ["vendor-pages"] });
      qc.invalidateQueries({ queryKey: ["source-operations"] });
    },
  });
}

/** Run a single scraper config now (manual trigger). */
export function useRunSource() {
  const qc = useQueryClient();
  return useMutation<RunResponse, Error, string>({
    mutationFn: (sourceId) =>
      apiPost<RunResponse>(`/api/v1/sources/${sourceId}/run`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["source-operations"] });
      qc.invalidateQueries({ queryKey: ["operator-status"] });
    },
  });
}

export function useSourceOperations(sourceId: string, enabled: boolean) {
  return useQuery<SourceOperations>({
    queryKey: sourceOperationsKey(sourceId),
    queryFn: () => apiGet<SourceOperations>(`/api/v1/sources/${sourceId}/operations`),
    enabled,
  });
}

export function useUpdateSourceOperations() {
  const qc = useQueryClient();
  return useMutation<
    SourceOperations,
    Error,
    { sourceId: string; patch: SourceOperationsUpdateRequest }
  >({
    mutationFn: ({ sourceId, patch }) =>
      apiPatch<SourceOperations>(`/api/v1/sources/${sourceId}/operations`, patch),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: SOURCES_KEY });
      qc.invalidateQueries({ queryKey: sourceOperationsKey(vars.sourceId) });
      qc.invalidateQueries({ queryKey: ["operator-status"] });
    },
  });
}

export function useSyncContentScraperSource() {
  const qc = useQueryClient();
  return useMutation<ContentScraperSyncResponse, Error, string>({
    mutationFn: (sourceId) =>
      apiPost<ContentScraperSyncResponse>(`/api/v1/sources/${sourceId}/scraper-sync`),
    onSuccess: (_data, sourceId) => {
      qc.invalidateQueries({ queryKey: sourceOperationsKey(sourceId) });
      qc.invalidateQueries({ queryKey: ["operator-status"] });
    },
  });
}

export function useClearCapturedContent() {
  const qc = useQueryClient();
  return useMutation<ClearCapturedContentResponse, Error, string>({
    mutationFn: (sourceId) =>
      apiPost<ClearCapturedContentResponse>(
        `/api/v1/sources/${sourceId}/clear-captured-content`,
        { confirm: "DELETE" },
      ),
    onSuccess: (_data, sourceId) => {
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: sourceOperationsKey(sourceId) });
      qc.invalidateQueries({ queryKey: ["operator-status"] });
    },
  });
}

/** Pause scheduled scraping for a source. */
export function usePauseSource() {
  const qc = useQueryClient();
  return useMutation<PauseResponse, Error, PauseArgs>({
    mutationFn: ({ sourceId, durationSeconds, until, reason }) => {
      const body: Record<string, unknown> = {};
      if (durationSeconds != null) body.duration_seconds = durationSeconds;
      if (until) body.until = until;
      if (reason) body.reason = reason;
      return apiPost<PauseResponse>(
        `/api/v1/sources/${sourceId}/pause`,
        body,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SOURCES_KEY });
      qc.invalidateQueries({ queryKey: ["source-operations"] });
      qc.invalidateQueries({ queryKey: ["operator-status"] });
    },
  });
}

/** Resume a paused source. Idempotent. */
export function useResumeSource() {
  const qc = useQueryClient();
  return useMutation<PauseResponse, Error, string>({
    mutationFn: (sourceId) =>
      apiPost<PauseResponse>(`/api/v1/sources/${sourceId}/resume`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SOURCES_KEY });
      qc.invalidateQueries({ queryKey: ["source-operations"] });
      qc.invalidateQueries({ queryKey: ["operator-status"] });
    },
  });
}

// ── CRUD (UI direct-edit path) ─────────────────────────────────────

export function useCreateSource() {
  const qc = useQueryClient();
  return useMutation<SourceConfig, Error, SourceCreateRequest>({
    mutationFn: (body) => apiPost<SourceConfig>("/api/v1/sources", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SOURCES_KEY });
    },
  });
}

export function useUpdateSource() {
  const qc = useQueryClient();
  return useMutation<
    SourceConfig,
    Error,
    { id: string; patch: SourceUpdateRequest }
  >({
    mutationFn: ({ id, patch }) =>
      apiPatch<SourceConfig>(`/api/v1/sources/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SOURCES_KEY });
    },
  });
}

export function useLinkSourceVendorPage() {
  const qc = useQueryClient();
  return useMutation<
    SourceConfig,
    Error,
    { sourceId: string; vendorPageId: string }
  >({
    mutationFn: ({ sourceId, vendorPageId }) =>
      apiPut<SourceConfig>(`/api/v1/sources/${sourceId}/vendor-page`, {
        vendor_page_id: vendorPageId,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: SOURCES_KEY });
      qc.invalidateQueries({ queryKey: ["operator-status"] });
      qc.invalidateQueries({ queryKey: sourceOperationsKey(vars.sourceId) });
    },
  });
}

export function useDeleteSource() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiDelete(`/api/v1/sources/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SOURCES_KEY });
    },
  });
}
